import { useState, type FormEvent } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Lock } from "lucide-react";
import { EVENTO, formatearMXN } from "@zociety/core";
import { reportar } from "@/lib/api";
import { APARIENCIA_STRIPE, IDIOMA_STRIPE, stripeCargado } from "@/lib/stripe";

/**
 * Paso de la tarjeta.
 *
 * Faltaba: el carrito abría el intento de pago y lo confirmaba acto seguido, lo
 * que solo funcionaba porque la pasarela de simulación aprueba sola. Con Stripe
 * de verdad, entre esas dos llamadas tiene que ocurrir esto.
 *
 * Los campos los pinta Stripe dentro de un iframe suyo. **El número de tarjeta
 * no pasa por nuestro código ni por nuestro dominio**, y por eso el backend
 * nunca queda dentro del alcance de PCI-DSS.
 */

function Formulario({
  montoCentavos,
  onPagado,
  onCancelar,
}: {
  montoCentavos: number;
  /** Se llama cuando Stripe confirmó el cobro; el carrito cierra la compra. */
  onPagado: () => void;
  onCancelar: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcesando(true);
    setError(null);

    /*
     * `redirect: "if_required"` mantiene el cobro en esta página.
     *
     * El intento se abre solo con métodos que no necesitan salir del sitio, así
     * que aquí nunca debería hacer falta redirigir. Si algún día se habilitan
     * OXXO o SPEI, este es uno de los puntos que cambia.
     */
    const r = await stripe.confirmPayment({ elements, redirect: "if_required" });

    if (r.error) {
      /*
       * No se repite lo que Stripe ya dijo.
       *
       * El Payment Element pinta los errores de tarjeta y de validación bajo el
       * campo correspondiente, que es donde la clienta está mirando. Repetirlos en
       * una franja aparte mostraba «tu tarjeta fue rechazada» dos veces y movía el
       * botón de pagar de sitio.
       *
       * Los demás tipos —fallo de red, límite de peticiones, petición inválida—
       * **no aparecen en ningún lado**, así que ahí esta franja es la única señal
       * y callarla dejaría el botón girando sin explicación.
       */
      const loDiceStripe = r.error.type === "card_error" || r.error.type === "validation_error";
      if (!loDiceStripe) {
        setError(r.error.message ?? "No pudimos procesar tu tarjeta. Intenta de nuevo.");
      }

      /*
       * Que la clienta no necesite verlo no significa que nosotros no.
       *
       * Este era el punto ciego: una compra de prueba falló aquí y en el servidor
       * no quedó ni una línea, porque el intercambio con la pasarela ocurre entre
       * el navegador y ella. Se investigó a partir de que alguien recordara qué
       * decía la pantalla.
       *
       * Una tarjeta rechazada se registra como información y una falla de red o
       * configuración como aviso: pasan cosas muy distintas y mezclarlas haría que
       * los rechazos normales tapen los problemas reales.
       */
      reportar(loDiceStripe ? EVENTO.PAGO_RECHAZADO : EVENTO.PAGO_ERROR_CLIENTE, r.error.message, {
        tipo_stripe: r.error.type,
        codigo: r.error.code ?? "",
        codigo_declinacion: r.error.decline_code ?? "",
        intento_id: r.error.payment_intent?.id ?? "",
      });

      setProcesando(false);
      return;
    }

    /*
     * No se acredita nada aquí. Quien decide si hubo cobro es el servidor,
     * consultándolo con Stripe: creer al navegador permitiría acreditar
     * paquetes sin pago.
     */
    onPagado();
  };

  return (
    <form className="pila g4" onSubmit={enviar}>
      <PaymentElement options={{ layout: "tabs" }} />

      {error && (
        <div className="aviso aviso-error" role="alert">
          {error}
        </div>
      )}

      <button className="btn btn-primario btn-bloque btn-lg" type="submit" disabled={procesando || !stripe}>
        {procesando && <span className="girador" />}
        <Lock size={15} aria-hidden />
        Pagar {formatearMXN(montoCentavos)}
      </button>

      <button className="btn btn-fantasma btn-sm" type="button" onClick={onCancelar} disabled={procesando}>
        Volver al carrito
      </button>

      <p className="diminuto tenue" style={{ textAlign: "center" }}>
        Tus datos de tarjeta los procesa Stripe. Zociety no los recibe ni los guarda.
      </p>
    </form>
  );
}

export default function PagoTarjeta({
  secretoCliente,
  secretoSesion,
  montoCentavos,
  onPagado,
  onCancelar,
}: {
  secretoCliente: string;
  /**
   * Permiso para mostrar las tarjetas guardadas de esta persona.
   *
   * Con él, el formulario ofrece las que ya tiene y una casilla para guardar la
   * nueva. Sin él —quien nunca ha comprado— pide la tarjeta como siempre.
   */
  secretoSesion?: string | null;
  montoCentavos: number;
  onPagado: () => void;
  onCancelar: () => void;
}) {
  return (
    <Elements
      stripe={stripeCargado()}
      options={{
        clientSecret: secretoCliente,
        ...(secretoSesion ? { customerSessionClientSecret: secretoSesion } : {}),
        appearance: APARIENCIA_STRIPE,
        locale: IDIOMA_STRIPE,
      }}
    >
      <Formulario montoCentavos={montoCentavos} onPagado={onPagado} onCancelar={onCancelar} />
    </Elements>
  );
}
